import type { Workspace } from '../types/workspace'

/**
 * Canonical predicate for "this workspace is a personal one".
 *
 * Reads BOTH `isPersonal` and `kind` fields (the two-truths data
 * model — see docs/plans/personal-workspace-in-member-app.md §0).
 * Every read site outside src/types, firestore.rules, and
 * functions/** MUST use this helper. New direct reads are blocked
 * by the `no-restricted-syntax` rule in eslint.config.js.
 */
export const isPersonalWorkspace = (
  workspace: Pick<Workspace, 'isPersonal' | 'kind'> | null | undefined,
): boolean => {
  if (!workspace) return false
  return workspace.isPersonal === true || workspace.kind === 'PERSONAL'
}

/**
 * Inverse of isPersonalWorkspace. Null / undefined workspaces are
 * treated as "not team" (returns false) so callers do not accidentally
 * enable team-only surfaces on a missing workspace.
 */
export const isTeamWorkspace = (
  workspace: Pick<Workspace, 'isPersonal' | 'kind'> | null | undefined,
): boolean => {
  if (!workspace) return false
  return !isPersonalWorkspace(workspace)
}

/**
 * Resolves the current user's personal workspace from an accessible workspace
 * list. The personal-workspace creation cap guarantees at most one match.
 */
export const resolvePersonalForUser = (
  uid: string | null | undefined,
  workspaces: readonly Pick<Workspace, 'id' | 'isPersonal' | 'kind' | 'ownerId' | 'slug'>[],
): Pick<Workspace, 'id' | 'isPersonal' | 'kind' | 'ownerId' | 'slug'> | null => {
  if (!uid) return null
  return workspaces.find((workspace) => isPersonalWorkspace(workspace) && workspace.ownerId === uid) ?? null
}
